import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useGlobalSound } from '@/hooks/useGlobalSound';

interface PendingTransaction {
  id: string;
  type: string;
  amount: number;
  created_at: string;
  status: string;
}

interface ChatMessage {
  id: string;
  message: string;
  sender: string;
  created_at: string;
  chat_id: string;
}

export default function GlobalAdminSoundAlert() {
  const [pendingTransactions, setPendingTransactions] = useState<PendingTransaction[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();
  const { isSoundEnabled, masterVolume } = useGlobalSound();

  // Initialize audio context
  useEffect(() => {
    const initAudio = async () => {
      try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        setAudioContext(ctx);
      } catch (error) {
        console.error('Error initializing audio context:', error);
      }
    };

    initAudio();

    return () => {
      if (audioContext) {
        audioContext.close();
      }
    };
  }, []);

  // Fetch pending transactions
  const fetchPendingTransactions = async () => {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('id, type, amount, created_at, status')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (!error && data) {
        setPendingTransactions(data);
        
        // Start alert if there are pending transactions and sound is enabled
        if (data.length > 0 && isSoundEnabled && !isPlaying) {
          startAlert();
        } else if (data.length === 0 && isPlaying) {
          stopAlert();
        }
      }
    } catch (error) {
      console.error('Error fetching pending transactions:', error);
    }
  };

  // Create alert sound using Web Audio API
  const createAlertSound = () => {
    if (!audioContext) return;

    try {
      // Resume audio context if suspended
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      const osc = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      // Connect nodes
      osc.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Configure oscillator (notification sound)
      osc.type = 'sine';
      osc.frequency.setValueAtTime(800, audioContext.currentTime); // High pitch
      osc.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + 0.1);
      osc.frequency.exponentialRampToValueAtTime(800, audioContext.currentTime + 0.2);
      
      // Configure gain (volume)
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(masterVolume * 0.3, audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      // Play sound
      osc.start(audioContext.currentTime);
      osc.stop(audioContext.currentTime + 0.3);
      
      return osc;
    } catch (error) {
      console.error('Error creating alert sound:', error);
      return null;
    }
  };

  // Create chat notification sound (different tone)
  const createChatNotificationSound = () => {
    if (!audioContext) return;

    try {
      // Resume audio context if suspended
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      const osc = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      // Connect nodes
      osc.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Configure oscillator (chat notification sound - higher pitch)
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1000, audioContext.currentTime); 
      osc.frequency.exponentialRampToValueAtTime(800, audioContext.currentTime + 0.05);
      osc.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.1);
      osc.frequency.exponentialRampToValueAtTime(900, audioContext.currentTime + 0.15);
      
      // Configure gain (volume)
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(masterVolume * 0.4, audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      
      // Play sound
      osc.start(audioContext.currentTime);
      osc.stop(audioContext.currentTime + 0.2);
      
      return osc;
    } catch (error) {
      console.error('Error creating chat notification sound:', error);
      return null;
    }
  };

  // Start continuous alert
  const startAlert = () => {
    if (!isSoundEnabled || isPlaying) return;

    setIsPlaying(true);
    
    // Create speech synthesis for Thai announcement
    const speakAlert = () => {
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(
          `แจ้งเตือน! มีธุรกรรมรอการอนุมัติจำนวน ${pendingTransactions.length} รายการ`
        );
        utterance.lang = 'th-TH';
        utterance.volume = masterVolume;
        utterance.rate = 1.0;
        utterance.pitch = 1.2;
        speechSynthesis.speak(utterance);
      }
    };

    // Play initial announcement
    speakAlert();
    
    // Set interval for continuous alert
    intervalRef.current = setInterval(() => {
      if (pendingTransactions.length > 0 && isSoundEnabled) {
        // Play beep sound
        createAlertSound();
        
        // Speak announcement every 30 seconds
        const now = Date.now();
        if (now % 30000 < 3000) { // Every 30 seconds (with 3 second tolerance)
          speakAlert();
        }
      }
    }, 3000); // Beep every 3 seconds

    console.log('🔊 Global alert started - pending transactions:', pendingTransactions.length);
  };

  // Play chat notification sound immediately
  const playChatNotification = () => {
    if (!isSoundEnabled) return;

    createChatNotificationSound();
    
    // Create speech synthesis for Thai announcement
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance('มีข้อความแชทใหม่จากลูกค้า');
      utterance.lang = 'th-TH';
      utterance.volume = masterVolume;
      utterance.rate = 1.0;
      utterance.pitch = 1.2;
      speechSynthesis.speak(utterance);
    }

    console.log('🔔 Chat notification played');
  };

  // Stop alert
  const stopAlert = () => {
    setIsPlaying(false);
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Stop any ongoing speech
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }

    console.log('🔇 Global alert stopped');
  };

  // Setup real-time listeners
  useEffect(() => {
    fetchPendingTransactions();

    // Listen for transaction changes
    const transactionChannel = supabase
      .channel('global-admin-transaction-alerts')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transactions'
        },
        (payload) => {
          console.log('🔔 Global transaction status changed:', payload);
          
          // Refresh pending transactions
          setTimeout(fetchPendingTransactions, 500);
          
          const transaction = payload.new;
          
          if (payload.eventType === 'UPDATE') {
            const txData = transaction as any;
            if (txData?.status === 'approved') {
              toast({
                title: "✅ ธุรกรรมได้รับการอนุมัติ",
                description: `${txData.type}: ฿${txData.amount?.toLocaleString()}`,
                duration: 5000,
              });
            } else if (txData?.status === 'failed') {
              toast({
                title: "❌ ธุรกรรมถูกปฏิเสธ",
                description: `${txData.type}: ฿${txData.amount?.toLocaleString()}`,
                duration: 5000,
              });
            }
          }
        }
      )
      .subscribe();

    // Listen for new chat messages from users (not admins)
    const chatChannel = supabase
      .channel('global-admin-chat-alerts')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_messages'
        },
        (payload) => {
          console.log('🔔 New chat message:', payload);
          
          const message = payload.new as ChatMessage;
          
          // Only alert if message is from user (not admin)
          if (message.sender === 'user') {
            playChatNotification();
            
            toast({
              title: "💬 ข้อความแชทใหม่",
              description: "มีลูกค้าส่งข้อความเข้ามา",
              duration: 5000,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(transactionChannel);
      supabase.removeChannel(chatChannel);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isSoundEnabled, masterVolume, pendingTransactions.length]);

  // Handle sound toggle from global state
  useEffect(() => {
    if (!isSoundEnabled && isPlaying) {
      stopAlert();
    } else if (isSoundEnabled && pendingTransactions.length > 0 && !isPlaying) {
      startAlert();
    }
  }, [isSoundEnabled]);

  // This component doesn't render anything - it's just for background functionality
  return null;
}